// The screen the user is on lives in the URL hash ("#handover/<project>/h/<handover>"), so a refresh
// returns to it and the browser's or the phone's back button steps back inside the application.
// Only ids are kept there, never data. Leaving unsaved changes is confirmed here too.
import { useSyncExternalStore } from 'react';
import { confirmDiscardPending, hasPendingChanges } from './master-data/unsaved-changes';

const listeners = new Set<() => void>();
/** The hash the screens show; the address bar is put back to it when leaving is cancelled. */
let shown: string | null = null;
/** Set by an in-app back button that already asked about unsaved changes. */
let confirmed = false;

const emit = () => {
  shown = window.location.hash;
  for (const listener of listeners) listener();
};

/** The screen of a hash: a step inside it ("…/v/<step>") stays on the same screen. */
const screenOf = (hash: string | null) => (hash ?? '').split('/v/')[0];

function onPopState() {
  const skip = confirmed;
  confirmed = false;
  if (window.location.hash === shown) return;
  // Moving between the steps of one screen keeps its changes: nothing to confirm.
  const sameScreen = screenOf(window.location.hash) === screenOf(shown);
  if (skip || sameScreen || !hasPendingChanges()) {
    emit();
    return;
  }
  // Stay until the user answers: the address bar goes back to the screen still shown (a new
  // entry in place of the one left). Leaving then steps back again, to the same entry the user
  // asked for, so the history keeps exactly its earlier steps.
  window.history.pushState({ sivan: true }, '', shown ?? '#');
  void confirmDiscardPending().then((leave) => {
    if (!leave) return;
    confirmed = true;
    window.history.back();
  });
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) {
    shown = window.location.hash;
    window.addEventListener('popstate', onPopState);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('popstate', onPopState);
  };
}

/** The current hash, e.g. "#handover/<project>". */
export function useHash(): string {
  return useSyncExternalStore(subscribe, () => window.location.hash);
}

/** Goes to a screen: `push` adds a step the back button returns from, `replace` does not. */
export function navigate(hash: string, mode: 'push' | 'replace'): void {
  if (window.location.hash === hash) return;
  if (mode === 'push') window.history.pushState({ sivan: true }, '', hash);
  else window.history.replaceState(window.history.state, '', hash);
  emit();
}

/**
 * An in-app back button (after its own unsaved-changes question): one step back in the history
 * when the previous step is this application's, otherwise (opened from a link or a bookmark) the
 * parent screen replaces the current one so the button never leaves the application.
 */
export function navigateBack(parent: string): void {
  if ((window.history.state as { sivan?: unknown } | null)?.sivan === true) {
    confirmed = true;
    window.history.back();
  } else {
    navigate(parent, 'replace');
  }
}
